import { useState } from 'react'
import {
  useDefects,
  useDeleteDefect,
  useFixDefect,
  useRejectDefect,
  useReopenDefect,
  useSaveDefect,
  useUsers
} from '../api/queries'
import type { Defect, DefectInput } from '../api/types'
import { useAuth } from '../auth/AuthProvider'
import { Avatar, Card, EmptyState, ErrorBox } from './ui'
import { PhotoPicker } from './PhotoPicker'
import { useAuthedBlob } from '../lib/photos'

function today() {
  const now = new Date()
  return new Date(now.getTime() - now.getTimezoneOffset() * 60000).toISOString().slice(0, 10)
}

function formatDate(value?: string | null) {
  if (!value) return '-'
  const [y, m, d] = value.slice(0, 10).split('-').map(Number)
  return new Date(y, m - 1, d).toLocaleDateString('da-DK', { day: 'numeric', month: 'short', year: 'numeric' })
}

/**
 * Fejl- og mangellisten på sagen (beslutning 058).
 *
 * Punkterne fra afleveringsforretningen: hvad mangler, hvor, hvem retter det, og
 * er det gjort. De åbne står øverst - det er dem, der skal handles på.
 *
 * Alle med adgang til sagen må skrive en mangel op; det er montøren på stedet,
 * der får den at vide. Kun en projektleder må afvise et punkt - det er en
 * beslutning om, hvem der hæfter.
 */
export function DefectsCard({ projectId }: { projectId: number }) {
  const { isProjectManager } = useAuth()
  const [editing, setEditing] = useState<Defect | null | undefined>(undefined)
  const [fixing, setFixing] = useState<Defect | null>(null)
  const [rejecting, setRejecting] = useState<Defect | null>(null)
  const [showClosed, setShowClosed] = useState(false)
  const [actionError, setActionError] = useState<string | null>(null)

  const { data, isLoading, error } = useDefects(projectId)
  const reopen = useReopenDefect(projectId)
  const remove = useDeleteDefect(projectId)

  // Er modulet slaaet fra, svarer API-et 404 - saa vises kortet slet ikke.
  if (error && String(error).includes('slået fra')) return null
  if (isLoading) return null

  const defects = data?.defects ?? []
  const visible = showClosed ? defects : defects.filter((d) => d.status === 'open')

  async function run(action: () => Promise<unknown>, fallback: string) {
    setActionError(null)
    try {
      await action()
    } catch (err) {
      setActionError(err instanceof Error ? err.message : fallback)
    }
  }

  return (
    <Card
      title={
        // "1 åben", ikke "1 åbne".
        `Fejl og mangler${
          data && data.open > 0 ? ` (${data.open} ${data.open === 1 ? 'åben' : 'åbne'})` : ''
        }`
      }
      actions={
        <div className="row" style={{ gap: 8 }}>
          {defects.length > 0 && (
            <label className="cal-person" style={{ cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={showClosed}
                onChange={(e) => setShowClosed(e.target.checked)}
              />
              Vis lukkede
            </label>
          )}
          <button className="btn btn--sm btn--primary" onClick={() => setEditing(null)}>
            Skriv mangel op
          </button>
        </div>
      }
    >
      {error && <ErrorBox error={error} />}
      {actionError && <ErrorBox error={actionError} />}

      {data && data.overdue > 0 && (
        <p className="small" style={{ marginTop: 0, color: '#DC2626' }}>
          <strong>
            {data.overdue} {data.overdue === 1 ? 'punkt er' : 'punkter er'} over fristen.
          </strong>
        </p>
      )}

      {defects.length === 0 && (
        <EmptyState>
          Der er ikke skrevet nogen mangler op. Punkterne fra afleveringsforretningen hører til her
          - så kommer de med i afleveringsmappen og bliver ikke glemt på en seddel.
        </EmptyState>
      )}

      {defects.length > 0 && visible.length === 0 && (
        <p className="small muted" style={{ marginTop: 0 }}>
          Alle {defects.length} punkter er lukket. Sæt flueben i <strong>Vis lukkede</strong> for at
          se dem.
        </p>
      )}

      <div className="stack" style={{ gap: 10 }}>
        {visible.map((d) => (
          <div
            key={d.id}
            className="row"
            style={{
              gap: 10,
              alignItems: 'flex-start',
              flexWrap: 'wrap',
              opacity: d.status === 'open' ? 1 : 0.7
            }}
          >
            <span
              style={{
                width: 6,
                alignSelf: 'stretch',
                minHeight: 40,
                borderRadius: 3,
                background: d.isOverdue ? '#DC2626' : d.color,
                flexShrink: 0
              }}
            />

            {/* Fotoet af manglen - og af udbedringen, naar den er lukket.
                Billederne ligger i sagens mappe som alle andre. */}
            {(d.photoFileId || d.fixedPhotoFileId) && (
              <div className="row" style={{ gap: 4 }}>
                {d.photoFileId && <DefectThumbnail projectId={projectId} fileId={d.photoFileId} />}
                {d.fixedPhotoFileId && (
                  <DefectThumbnail projectId={projectId} fileId={d.fixedPhotoFileId} />
                )}
              </div>
            )}

            <div style={{ minWidth: 200, flex: 1 }}>
              <div style={{ fontWeight: 600 }}>
                <span className="muted">{d.number}.</span> {d.title}
              </div>
              <div className="small muted">
                {d.location ?? 'Ingen placering'}
                {d.raisedBy && ` · påpeget af ${d.raisedBy}`}
                {` · ${formatDate(d.raisedOn)}`}
              </div>
              {d.description && <div className="small">{d.description}</div>}
              {d.status === 'rejected' && d.rejectionReason && (
                <div className="small" style={{ color: '#B45309' }}>
                  Afvist: {d.rejectionReason}
                </div>
              )}
              {d.status === 'fixed' && (
                <div className="tiny muted">
                  Udbedret {formatDate(d.fixedOn)}
                  {d.fixedBy && ` af ${d.fixedBy.displayName}`}
                </div>
              )}
            </div>

            <div style={{ minWidth: 150 }}>
              {d.assignedUser ? (
                <div className="row" style={{ gap: 6, alignItems: 'center' }}>
                  <Avatar user={d.assignedUser} size={24} />
                  <div className="small">{d.assignedUser.displayName}</div>
                </div>
              ) : (
                <div className="small muted">Ikke fordelt</div>
              )}
              {d.deadline && (
                <div
                  className="tiny"
                  style={{ color: d.isOverdue ? '#DC2626' : undefined, fontWeight: d.isOverdue ? 600 : 400 }}
                >
                  Frist {formatDate(d.deadline)}
                </div>
              )}
            </div>

            <div className="row" style={{ gap: 6 }}>
              {d.status === 'open' ? (
                <>
                  <button className="btn btn--sm" onClick={() => setFixing(d)}>
                    Udbedret
                  </button>
                  <button className="btn btn--sm" onClick={() => setEditing(d)}>
                    Ret
                  </button>
                  {isProjectManager && (
                    <button className="btn btn--sm" onClick={() => setRejecting(d)}>
                      Afvis
                    </button>
                  )}
                </>
              ) : (
                <button
                  className="btn btn--sm"
                  onClick={() => void run(() => reopen.mutateAsync(d.id), 'Punktet kunne ikke genåbnes.')}
                >
                  Genåbn
                </button>
              )}

              {isProjectManager && (
                <button
                  className="btn btn--danger btn--sm"
                  onClick={() => {
                    if (
                      confirm(
                        `Slet punkt ${d.number}?\n\n` +
                          'Er punktet reelt, men ikke jeres ansvar, så brug Afvis med en begrundelse i stedet - ' +
                          'så kan det ses, at der blev taget stilling.'
                      )
                    ) {
                      void run(() => remove.mutateAsync(d.id), 'Punktet kunne ikke slettes.')
                    }
                  }}
                >
                  Slet
                </button>
              )}
            </div>
          </div>
        ))}
      </div>

      {editing !== undefined && (
        <DefectDialog projectId={projectId} defect={editing} onClose={() => setEditing(undefined)} />
      )}
      {fixing && <FixDialog projectId={projectId} defect={fixing} onClose={() => setFixing(null)} />}
      {rejecting && (
        <RejectDialog projectId={projectId} defect={rejecting} onClose={() => setRejecting(null)} />
      )}
    </Card>
  )
}

/**
 * Lille miniature af et foto paa manglen. Hentes bag authentication - billederne
 * ligger i SharePoint og kan ikke naas med et almindeligt img-tag.
 */
function DefectThumbnail({ projectId, fileId }: { projectId: number; fileId: number }) {
  const url = useAuthedBlob(`/api/projects/${projectId}/files/${fileId}/thumbnail`)

  return (
    <div
      style={{
        width: 44,
        height: 44,
        borderRadius: 6,
        background: url ? `center/cover url(${url})` : 'var(--surface-2)',
        flexShrink: 0
      }}
    />
  )
}

function Modal({ children, onClose }: { children: React.ReactNode; onClose: () => void }) {
  return (
    <div
      className="modal"
      role="dialog"
      aria-modal="true"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose()
      }}
    >
      <div className="modal__card">{children}</div>
    </div>
  )
}

function DefectDialog({
  projectId,
  defect,
  onClose
}: {
  projectId: number
  defect: Defect | null
  onClose: () => void
}) {
  const { data: users } = useUsers()
  const save = useSaveDefect(projectId)
  const [form, setForm] = useState<DefectInput>({
    title: defect?.title ?? '',
    location: defect?.location ?? '',
    description: defect?.description ?? '',
    assignedUserId: defect?.assignedUser?.id ?? null,
    deadline: defect?.deadline ?? '',
    raisedBy: defect?.raisedBy ?? '',
    raisedOn: defect?.raisedOn ?? today(),
    photoFileId: defect?.photoFileId ?? null
  })
  const [error, setError] = useState<string | null>(null)

  async function submit(e: React.FormEvent) {
    e.preventDefault()
    setError(null)
    try {
      await save.mutateAsync({
        id: defect?.id,
        body: {
          ...form,
          location: form.location?.trim() || null,
          description: form.description?.trim() || null,
          deadline: form.deadline || null,
          raisedBy: form.raisedBy?.trim() || null
        }
      })
      onClose()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Punktet kunne ikke gemmes.')
    }
  }

  return (
    <Modal onClose={onClose}>
      <h2 style={{ margin: '0 0 14px', fontSize: 18 }}>
        {defect ? `Ret punkt ${defect.number}` : 'Skriv mangel op'}
      </h2>

      <form onSubmit={(e) => void submit(e)}>
        <label className="field">
          <span className="field__label">Hvad mangler</span>
          <input
            value={form.title}
            required
            placeholder="F.eks. Stikkontakt sidder skævt"
            onChange={(e) => setForm({ ...form, title: e.target.value })}
          />
        </label>

        <label className="field">
          <span className="field__label">Hvor</span>
          <input
            value={form.location ?? ''}
            placeholder="F.eks. Køkken, ved vinduet"
            onChange={(e) => setForm({ ...form, location: e.target.value })}
          />
        </label>

        <label className="field">
          <span className="field__label">Uddybning (valgfri)</span>
          <textarea
            rows={2}
            value={form.description ?? ''}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
          />
        </label>

        <PhotoPicker
          projectId={projectId}
          label="Foto af manglen (valgfri)"
          value={form.photoFileId}
          onChange={(id) => setForm({ ...form, photoFileId: id })}
        />

        <div className="row" style={{ gap: 10 }}>
          <label className="field" style={{ flex: 1 }}>
            <span className="field__label">Hvem retter det</span>
            <select
              value={form.assignedUserId ?? 0}
              onChange={(e) =>
                setForm({ ...form, assignedUserId: Number(e.target.value) || null })
              }
            >
              <option value={0}>Ikke fordelt endnu</option>
              {(users ?? []).filter((u) => u.isActive).map((u) => (
                <option key={u.id} value={u.id}>
                  {u.displayName}
                </option>
              ))}
            </select>
          </label>

          <label className="field" style={{ flex: 1 }}>
            <span className="field__label">Frist</span>
            <input
              type="date"
              value={form.deadline ?? ''}
              onChange={(e) => setForm({ ...form, deadline: e.target.value })}
            />
          </label>
        </div>

        <div className="row" style={{ gap: 10 }}>
          <label className="field" style={{ flex: 1 }}>
            <span className="field__label">Påpeget af</span>
            <input
              value={form.raisedBy ?? ''}
              placeholder="Bygherre, tilsyn eller eget navn"
              onChange={(e) => setForm({ ...form, raisedBy: e.target.value })}
            />
          </label>
          <label className="field" style={{ flex: 1 }}>
            <span className="field__label">Dato</span>
            <input
              type="date"
              value={form.raisedOn ?? ''}
              max={today()}
              onChange={(e) => setForm({ ...form, raisedOn: e.target.value })}
            />
          </label>
        </div>

        <p className="tiny muted" style={{ marginTop: 0 }}>
          Punktet kommer med i afleveringsmappen - både mens det er åbent, og når det er lukket.
        </p>

        {error && <ErrorBox error={error} />}

        <div className="row" style={{ gap: 8, marginTop: 12 }}>
          <button className="btn btn--primary" type="submit" disabled={save.isPending}>
            {save.isPending ? 'Gemmer ...' : 'Gem'}
          </button>
          <button className="btn" type="button" onClick={onClose}>
            Annullér
          </button>
        </div>
      </form>
    </Modal>
  )
}

function FixDialog({
  projectId,
  defect,
  onClose
}: {
  projectId: number
  defect: Defect
  onClose: () => void
}) {
  const fix = useFixDefect(projectId)
  const [fixedOn, setFixedOn] = useState(today())
  const [note, setNote] = useState('')
  const [fixedPhotoFileId, setFixedPhotoFileId] = useState<number | null>(null)
  const [error, setError] = useState<string | null>(null)

  async function submit(e: React.FormEvent) {
    e.preventDefault()
    setError(null)
    try {
      await fix.mutateAsync({
        id: defect.id,
        fixedOn,
        note: note.trim() || null,
        fixedPhotoFileId
      })
      onClose()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Punktet kunne ikke markeres som udbedret.')
    }
  }

  return (
    <Modal onClose={onClose}>
      <h2 style={{ margin: '0 0 6px', fontSize: 18 }}>Punkt {defect.number} er udbedret</h2>
      <p className="small muted" style={{ marginTop: 0 }}>
        {defect.title}
        {defect.location && ` · ${defect.location}`}
      </p>

      <form onSubmit={(e) => void submit(e)}>
        <label className="field">
          <span className="field__label">Udbedret</span>
          <input
            type="date"
            value={fixedOn}
            max={today()}
            onChange={(e) => setFixedOn(e.target.value)}
          />
        </label>

        <label className="field">
          <span className="field__label">Hvad blev gjort (valgfri)</span>
          <input value={note} onChange={(e) => setNote(e.target.value)} />
        </label>

        {/* Beviset paa, at punktet er lukket - det er dét, bygherren spoerger om. */}
        <PhotoPicker
          projectId={projectId}
          label="Foto af udbedringen (valgfri)"
          value={fixedPhotoFileId}
          onChange={setFixedPhotoFileId}
        />

        {error && <ErrorBox error={error} />}

        <div className="row" style={{ gap: 8, marginTop: 12 }}>
          <button className="btn btn--primary" type="submit" disabled={fix.isPending}>
            {fix.isPending ? 'Gemmer ...' : 'Markér som udbedret'}
          </button>
          <button className="btn" type="button" onClick={onClose}>
            Annullér
          </button>
        </div>
      </form>
    </Modal>
  )
}

function RejectDialog({
  projectId,
  defect,
  onClose
}: {
  projectId: number
  defect: Defect
  onClose: () => void
}) {
  const reject = useRejectDefect(projectId)
  const [reason, setReason] = useState('')
  const [error, setError] = useState<string | null>(null)

  async function submit(e: React.FormEvent) {
    e.preventDefault()
    setError(null)
    try {
      await reject.mutateAsync({ id: defect.id, reason: reason.trim() })
      onClose()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Punktet kunne ikke afvises.')
    }
  }

  return (
    <Modal onClose={onClose}>
      <h2 style={{ margin: '0 0 6px', fontSize: 18 }}>Afvis punkt {defect.number}</h2>
      <p className="small muted" style={{ marginTop: 0 }}>
        {defect.title}
      </p>

      <form onSubmit={(e) => void submit(e)}>
        <label className="field">
          <span className="field__label">Hvorfor er det ikke jeres ansvar</span>
          <textarea
            rows={3}
            value={reason}
            required
            placeholder="F.eks. Udført efter aftalt tegning - ændringen er bestilt af bygherren efterfølgende"
            onChange={(e) => setReason(e.target.value)}
          />
        </label>

        <p className="tiny muted" style={{ marginTop: 0 }}>
          Punktet slettes ikke - det bliver stående med begrundelsen og kommer med i
          afleveringsmappen. Bygherren har skrevet det op, og så skal det kunne ses, at der blev
          taget stilling.
        </p>

        {error && <ErrorBox error={error} />}

        <div className="row" style={{ gap: 8, marginTop: 12 }}>
          <button className="btn btn--primary" type="submit" disabled={reject.isPending || !reason.trim()}>
            {reject.isPending ? 'Gemmer ...' : 'Afvis punktet'}
          </button>
          <button className="btn" type="button" onClick={onClose}>
            Annullér
          </button>
        </div>
      </form>
    </Modal>
  )
}
